/**
 * CSS processing utilities for Viewer Express API
 */

import scope from "scope-css";
import type { ThemeVariant } from "../types";
import {
  buildPopoverThemeColorCSS,
  generatePopoverThemeCSS,
  isOpaqueBackground,
  resolvePopoverThemeColors,
} from "../adapters/utils/themeUtils";
import { adaptThemeVwToLayoutUnit, LAYOUT_VIEWPORT_WIDTH_FORMULA } from "./layoutViewport";

const VIEWER_EXPRESS_CONTAINER_SELECTOR = "#viewer-express-container";

const INLINE_MATH_EXPRESS_OVERRIDES = `
${VIEWER_EXPRESS_CONTAINER_SELECTOR} .spf-inline-expression .math-align {
  top: 0 !important;
}
`;

// Reader font settings scope to #viewer-express-container * (ID + universal), which beats
// component class selectors. Keep code block editor on a fixed monospace stack.
const CODE_BLOCK_FONT_OVERRIDES = `
${VIEWER_EXPRESS_CONTAINER_SELECTOR} .codeEditorContainer,
${VIEWER_EXPRESS_CONTAINER_SELECTOR} .codeEditorContainer * {
  font-family: monospace !important;
}
`;

// dev-shell * { padding: 0 } was flattening nested lists when express mounts in-page.
const VIEWER_LIST_PADDING_RESTORE_CSS = `
${VIEWER_EXPRESS_CONTAINER_SELECTOR} ul,
${VIEWER_EXPRESS_CONTAINER_SELECTOR} ol {
  padding-inline-start: revert;
}
`;

// AutoInsertLists UL/OL/Para and StyledCaption use margin: 0; types only, so the theme's
// class rules still win as they do in BV.
const VIEWER_MARGIN_RESTORE_CSS = `
body ${VIEWER_EXPRESS_CONTAINER_SELECTOR} ul,
body ${VIEWER_EXPRESS_CONTAINER_SELECTOR} ol,
body ${VIEWER_EXPRESS_CONTAINER_SELECTOR} li > p,
body ${VIEWER_EXPRESS_CONTAINER_SELECTOR} table caption p {
  margin: 0;
}
`;

const CONTAINER_CSS = `
${VIEWER_EXPRESS_CONTAINER_SELECTOR} {
  --layout-viewport-width: ${LAYOUT_VIEWPORT_WIDTH_FORMULA};
  --vw: calc(var(--layout-viewport-width) / 100);
  width: var(--layout-viewport-width);
  box-sizing: border-box;
  height: 100%;
  min-width: 0;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
}
${INLINE_MATH_EXPRESS_OVERRIDES}
${VIEWER_LIST_PADDING_RESTORE_CSS}
${VIEWER_EXPRESS_CONTAINER_SELECTOR} .stage-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden auto;
}
${VIEWER_EXPRESS_CONTAINER_SELECTOR} .stage-box {
  width: 100%;
  position: relative;
}
`;

/**
 * Injects base container styles for viewer-express-container.
 */
export function injectContainerStyles(): void {
  // Remove existing container styles if present
  const existingStyle = document.getElementById("viewer-express-container-styles");
  if (existingStyle) {
    existingStyle.remove();
  }

  // Create and inject style element
  const styleElement = document.createElement("style");
  styleElement.id = "viewer-express-container-styles";
  styleElement.textContent = CONTAINER_CSS;
  document.head.appendChild(styleElement);
}

/**
 * Extracts background color from theme variant CSS.
 * Returns the background color string or null if not found.
 */
function extractBackgroundColor(
  themeVariant: Partial<ThemeVariant> | null | undefined,
): string | null {
  if (!themeVariant) {
    return null;
  }

  let bgColor: string | null = null;

  // Get backgroundColor from theme variant CSS, specifically from .spf-rce-section-content
  if (themeVariant.css) {
    // First, try to find background-color in .spf-rce-section-content selector
    const sectionContentBgMatch = themeVariant.css.match(
      /\.spf-rce-section-content\s*\{[^}]*background(?:-color)?:\s*([^;]+)/i,
    );
    if (sectionContentBgMatch) {
      const extractedColor = sectionContentBgMatch[1].trim();
      // Only apply if it's a valid color value (not a gradient or image)
      if (!extractedColor.includes("url(") && !extractedColor.includes("gradient")) {
        bgColor = extractedColor.replace(/!important/gi, "").trim();
      }
    }

    // Fallback: try to find background-color in body selector
    if (!bgColor) {
      const bodyBgMatch = themeVariant.css.match(/body\s*\{[^}]*background(?:-color)?:\s*([^;]+)/i);
      if (bodyBgMatch) {
        const extractedColor = bodyBgMatch[1].trim();
        if (!extractedColor.includes("url(") && !extractedColor.includes("gradient")) {
          bgColor = extractedColor.replace(/!important/gi, "").trim();
        }
      }
    }

    // Final fallback: look for any background-color declaration
    if (!bgColor) {
      const backgroundColorMatch = themeVariant.css.match(/background(?:-color)?:\s*([^;]+)/i);
      if (backgroundColorMatch) {
        const extractedColor = backgroundColorMatch[1].trim();
        if (!extractedColor.includes("url(") && !extractedColor.includes("gradient")) {
          bgColor = extractedColor.replace(/!important/gi, "").trim();
        }
      }
    }
  }

  // Also check if backgroundColor is directly on theme variant object
  if (!bgColor && themeVariant.backgroundColor) {
    bgColor = themeVariant.backgroundColor;
  }

  return bgColor;
}

/**
 * In BV a plugin renders in its own iframe whose <body> carries the plugin class, so theme
 * rules like "body.plugin-eText-MCQ label.option" target the plugin root. Express renders
 * the plugin inline with that class on its wrapper: turn "<scope> body.plugin-X" into
 * "body <scope> .plugin-X" (descendant), not "body <scope>.plugin-X" which would need the
 * class on the container itself and never matches.
 */
function scopePluginBodySelectors(scoped: string, scopeSelector: string): string {
  const escaped = scopeSelector.replace(/[.#]/g, "\\$&");
  return scoped.replace(
    new RegExp(`${escaped} body(\\.plugin-[\\w-]+)`, "g"),
    `body ${scopeSelector} $1`,
  );
}

/**
 * Scopes CSS to #viewer-express-container using the scope-css library.
 * Every selector is prefixed with the container so rules only apply inside it.
 * Use this when you want library-based scoping instead of the custom scopeCSSToContainer.
 */
export function scopeCSSToContainer2(css: string): string {
  if (!css || typeof css !== "string") {
    return "";
  }
  const scoped = scopePluginBodySelectors(
    scope(css, VIEWER_EXPRESS_CONTAINER_SELECTOR, { keyframes: true }),
    VIEWER_EXPRESS_CONTAINER_SELECTOR,
  );

  // Rewrite "#viewer-express-container body ..." to "body #viewer-express-container ...":
  // the container always sits inside <body> so the match set is unchanged, and keeping
  // the body token preserves the one type-selector of specificity it contributes.
  // Collapsing it away made theme rules like "body ul li" (0,0,3 in BV, where ties are
  // resolved by source order) lose to sibling rules like "ul li li" after scoping.
  const scopedReplaced = scoped.replace(
    /#viewer-express-container body/gi,
    `body ${VIEWER_EXPRESS_CONTAINER_SELECTOR}`,
  );

  return scopedReplaced;
}

const POPOVER_CSS_STYLE_ID = "viewer-express-popover-css";
const VIEWER_EXPRESS_CONTAINER_ID = "viewer-express-container";
const POPUP_TIP_SELECTOR = ".popupTip";

/**
 * Re-scopes theme CSS for popupTips that may render outside #viewer-express-container.
 * e.g. ".spf-element-image span.component-label { color: #245194 }" becomes
 * ".popupTip .spf-element-image span.component-label { color: #245194 }"
 * so font-family, color, font-size, etc. follow the active theme variant.
 */
function scopeCSSToPopupTip(css: string): string {
  if (!css || typeof css !== "string") {
    return "";
  }
  const scoped = scopePluginBodySelectors(
    scope(css, POPUP_TIP_SELECTOR, { keyframes: true }),
    POPUP_TIP_SELECTOR,
  );

  return scoped.replace(/\.popupTip body/gi, `body ${POPUP_TIP_SELECTOR}`);
}

function resolvePopoverColorsFromViewer(): { surface: string; text: string } | null {
  if (typeof document === "undefined") return null;

  const container = document.getElementById(VIEWER_EXPRESS_CONTAINER_ID);
  if (!container) return null;

  const textSample =
    container.querySelector<HTMLElement>(".spf-rce-section-content p") ||
    container.querySelector<HTMLElement>("p") ||
    container.querySelector<HTMLElement>(".spf-rce-section-content") ||
    container;

  let surface = "";
  let surfaceEl: HTMLElement | null =
    container.querySelector<HTMLElement>(".spf-rce-section-content") || container;
  while (surfaceEl) {
    const candidate = getComputedStyle(surfaceEl).backgroundColor;
    if (isOpaqueBackground(candidate)) {
      surface = candidate;
      break;
    }
    surfaceEl = surfaceEl.parentElement;
  }

  const text = getComputedStyle(textSample).color;

  if (!surface || !text) {
    return null;
  }

  return { surface, text };
}

function buildPopoverCSS(themeVariant: Partial<ThemeVariant> | null | undefined): string {
  const readerCss = themeVariant?.popoverCss ?? "";
  const liveColors = resolvePopoverColorsFromViewer();
  const themeColors = liveColors ?? (themeVariant ? resolvePopoverThemeColors(themeVariant) : null);
  const themeCss = themeColors
    ? buildPopoverThemeColorCSS(themeColors.surface, themeColors.text, themeVariant?.variantName)
    : themeVariant
    ? generatePopoverThemeCSS(themeVariant)
    : "";

  const popupTipThemeCss = themeVariant?.css
    ? scopeCSSToPopupTip(adaptThemeVwToLayoutUnit(themeVariant.css))
    : "";

  return `${readerCss}${themeCss}${popupTipThemeCss}`;
}

function injectPopoverCSS(themeVariant: Partial<ThemeVariant> | null | undefined): void {
  const popoverCss = buildPopoverCSS(themeVariant);

  const existingStyle = document.getElementById(POPOVER_CSS_STYLE_ID);

  if (!popoverCss) {
    existingStyle?.remove();
    return;
  }

  if (existingStyle) {
    existingStyle.textContent = popoverCss;
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.id = POPOVER_CSS_STYLE_ID;
  styleElement.textContent = popoverCss;
  document.head.appendChild(styleElement);
}

/**
 * Injects theme CSS into the document head, scoped to #viewer-express-container.
 * Uses double-buffering technique to prevent flickering during theme switches.
 * Now includes background color in the scoped CSS for better optimization.
 */
export function injectThemeCSS(themeVariant: Partial<ThemeVariant> | null | undefined): void {
  // Inject theme CSS into document head
  if (themeVariant && themeVariant.css) {
    const layoutAwareCss = adaptThemeVwToLayoutUnit(themeVariant.css);
    let scopedCSS = scopeCSSToContainer2(layoutAwareCss);

    // Extract and add background color to scoped CSS if found
    const bgColor = extractBackgroundColor(themeVariant);
    if (bgColor) {
      // Check if #viewer-express-container already exists in the scoped CSS
      const containerSelectorRegex = new RegExp(
        `${VIEWER_EXPRESS_CONTAINER_SELECTOR.replace("#", "\\#")}\\s*\\{([^}]*)\\}`,
      );
      const containerMatch = scopedCSS.match(containerSelectorRegex);

      if (containerMatch) {
        // Update existing container selector
        const existingProps = containerMatch[1];
        // Remove existing background-color if present
        let cleanedProps = existingProps.replace(/background(?:-color)?:\s*[^;]+;?/gi, "").trim();
        // Ensure proper spacing - add space if there are existing properties
        if (cleanedProps && !cleanedProps.endsWith(";")) {
          cleanedProps += ";";
        }
        const separator = cleanedProps ? " " : "";
        scopedCSS = scopedCSS.replace(
          containerSelectorRegex,
          `${VIEWER_EXPRESS_CONTAINER_SELECTOR} {${cleanedProps}${separator}background-color: ${bgColor};}`,
        );
      } else {
        // Prepend container selector with background color
        scopedCSS = `${VIEWER_EXPRESS_CONTAINER_SELECTOR} { background-color: ${bgColor}; }\n${scopedCSS}`;
      }
    }

    scopedCSS += `\n${INLINE_MATH_EXPRESS_OVERRIDES}`;
    scopedCSS += `\n${CODE_BLOCK_FONT_OVERRIDES}`;
    scopedCSS += `\n${VIEWER_MARGIN_RESTORE_CSS}`;

    const existingStyle = document.getElementById("viewer-express-theme-css");

    if (existingStyle) {
      // Use double-buffering: create new style element first, then remove old one
      // This prevents any gap where CSS is missing
      const newStyleElement = document.createElement("style");
      newStyleElement.id = "viewer-express-theme-css-temp";
      newStyleElement.textContent = scopedCSS;

      // Insert new style element right before the old one to maintain order
      existingStyle.parentNode?.insertBefore(newStyleElement, existingStyle);

      // Force a synchronous reflow to ensure new styles are applied before removing old ones
      void newStyleElement.offsetHeight;

      // Now remove the old style element and update the new one's ID
      existingStyle.remove();
      newStyleElement.id = "viewer-express-theme-css";
      console.log("Theme CSS updated");
    } else {
      // Create and inject style element if it doesn't exist
      const styleElement = document.createElement("style");
      styleElement.id = "viewer-express-theme-css";
      styleElement.textContent = scopedCSS;
      document.head.appendChild(styleElement);
      console.log("Theme CSS injected");
    }

    // Ensure scoped theme rules are applied before sampling live reader colors.
    const viewerContainer = document.getElementById(VIEWER_EXPRESS_CONTAINER_ID);
    void viewerContainer?.offsetHeight;
    injectPopoverCSS(themeVariant);
    requestAnimationFrame(() => injectPopoverCSS(themeVariant));
  } else {
    // Remove theme CSS if no CSS is provided
    const existingStyle = document.getElementById("viewer-express-theme-css");
    // console.log("removing theme css");
    if (existingStyle) {
      existingStyle.remove();
    }
    injectPopoverCSS(null);
  }
}
